import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, Button } from '../UI';
import { usePopper } from 'react-popper';
import { useTranslation } from 'react-i18next';
import { debounce, isEmpty } from 'lodash';
import { CheckboxWithLabel } from '../Widget/EconomicCalendar/Utils';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { useQuery } from '@apollo/client';
import { LIST_ASSETS_EXCHANGES } from '../../http/apollo-queries';
import { SearchAssetsInput } from '../Widget/HistoricalIndex/SearchAssetsInput';

export function transformFiltersToCheckbox(
  filters: AssetExchangeFilter[],
  groupKey: string,
  optionKey: string
): Filter[] {
  const groupedFilters = groupEventFilters(filters, groupKey);
  const groups = Object.keys(groupedFilters);

  return groups.map((group) => {
    return {
      isChecked: false,
      id: `${groupKey}|${group}`,
      title: group,
      value: '',
      options: groupedFilters[group].map((option) => ({
        isChecked: false,
        id: `${optionKey}|${option.name}_${option.code}`,
        label: `${option.name}_${option.code}`,
        value: option.name,
        ...option,
      })),
    };
  });
}

export function groupEventFilters(
  filters: AssetExchangeFilter[],
  groupKey: string
): {} {
  const groups = {};

  filters.forEach((filter) => {
    const groupId = filter[groupKey];
    groups[groupId] = groups[groupId] || [];
    groups[groupId].push(filter);
  });

  return groups;
}

interface ExchangeFilterProps {
  width: number;
  onFiltersChange: (selectedFilters: string[]) => void;
  groupKey?: string;
  optionKey?: string;
  attrToCheck?: string[];
  defaultCountry?: string;
}

type CheckedEl = {
  isRoot: boolean;
  id: string;
  parentId: string | null;
  value: string;
};

type AssetExchangeFilter = {
  name: string;
  code: string;
  country: string;
};

const emptyFilters = [];

type Filter = {
  isChecked: boolean;
  title: string;
  id: string;
  value: string;
  options: (AssetExchangeFilter & {
    isChecked: boolean;
    id: string;
    label: string;
    value: string;
  })[];
};

const TreeFilter: React.FC<ExchangeFilterProps> = ({
  width,
  attrToCheck = ['code', 'country', 'name'],
  optionKey = 'exchange',
  groupKey = 'country',
  onFiltersChange,
  defaultCountry,
}) => {
  const { t } = useTranslation();

  // Calendar filters
  const { data: { listAssetsExchanges: filtersData = [] } = {}, loading } =
    useQuery<{
      listAssetsExchanges: AssetExchangeFilter[];
    }>(LIST_ASSETS_EXCHANGES);

  // Modal
  const [isVisible, setIsVisible] = useState(false);

  const [referenceEl, setReferenceEl] = useState<HTMLDivElement | null>(null);
  const [filtersEl, setFiltersEl] = useState<HTMLDivElement | null>(null);
  const filtersRef = useRef<HTMLDivElement | null>(null);

  const popper = usePopper(referenceEl, filtersEl, {
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left'],
        },
      },
    ],
  });

  // Filters
  const [filters, setFilters] = useState<Filter[]>([]);
  const [initialFilters, setInitialFilters] = useState<Filter[]>([]);
  const [checked, setChecked] = useState<CheckedEl[]>(emptyFilters);
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    if (isEmpty(filtersData)) return;

    const filters = transformFiltersToCheckbox(
      filtersData,
      groupKey,
      optionKey
    ) as Filter[];
    setFilters(filters);
    setInitialFilters(filters);

    if (defaultCountry) {
      const checked: CheckedEl[] = filtersData
        .filter((f) => f.country === defaultCountry)
        .map(
          (f) =>
            ({
              isRoot: false,
              id: `exchange|${f.name}_${f.code}`,
              parentId: `country|${f.country}`,
              value: f.name,
            } as CheckedEl)
        )
        .concat([
          {
            isRoot: true,
            id: `country|${defaultCountry}`,
            parentId: null,
            value: defaultCountry,
          },
        ]);

      setChecked(checked);
    }
  }, [filtersData]);

  useEffect(() => {
    popper.update?.();
  }, [width]);

  const onCheckedChange = useCallback(
    debounce((args) => {
      onFiltersChange(args);
    }, 500),
    []
  );

  useEffect(() => {
    onCheckedChange(checked.filter((el) => !el.isRoot).map((el) => el.value));
  }, [checked, onFiltersChange]);

  useEffect(() => {
    // Reset filters
    if (!searchFilter.trim()) {
      return setFilters(
        transformFiltersToCheckbox(filtersData, groupKey, optionKey)
      );
    }

    // Match filter with search term
    const matches = initialFilters
      .map((filter) => filter.options)
      .flat()
      .filter((el) =>
        attrToCheck.some((attr) =>
          el[attr].toLowerCase().includes(searchFilter.toLowerCase())
        )
      );

    setFilters(transformFiltersToCheckbox(matches, groupKey, optionKey));
  }, [searchFilter]);

  useOutsideClick(filtersRef, () => {
    setIsVisible(false);
  });

  return (
    <div className={'flex items-center w-full h-full'} ref={filtersRef}>
      {/* POPPER */}
      <div
        ref={setFiltersEl}
        style={popper.styles.popper}
        {...popper.attributes.popper}
        className={`${isVisible ? 'block' : 'hidden'} w-96 h-80 z-100`}
      >
        <div className="w-full h-full bg-white border border-grey-10 rounded-xl flex flex-col dark:bg-grey-90 dark:border-grey-70">
          <SearchAssetsInput
            placeholder={t('markets.searchExchange')}
            onFinishTyping={setSearchFilter}
            loading={loading}
          />
          <div
            className={`w-full flex-1 text-black overflow-y-auto p-4 ${
              loading && 'pointer-events-none bg-grey-backdrop'
            }`}
          >
            <Button
              onClick={() => setChecked(emptyFilters)}
              variant={'secondary'}
            >
              {t('markets.removeFilters')}
            </Button>

            <div>
              {filters.map((filter, i) => {
                const isRootChecked =
                  checked.findIndex((el) => el.id === filter.id) !== -1;
                return (
                  <div key={i}>
                    <div className="flex w-full items-center justify-between mt-1 py-2">
                      <CheckboxWithLabel
                        isChecked={isRootChecked}
                        onClick={() => {
                          if (isRootChecked) {
                            // Remove all checkboxes from this category
                            const otherOnes = checked
                              .filter((el) => el.parentId !== filter.id)
                              .filter((el) => el.id !== filter.id);
                            setChecked(otherOnes);
                            return;
                          } else {
                            const isFullyChecked =
                              initialFilters.find(({ id }) => filter.id === id)
                                ?.options?.length === filter.options.length;

                            // Mark all as checked for this category
                            setChecked((prev) => [
                              // Save root as marked
                              ...(isFullyChecked
                                ? [
                                    {
                                      isRoot: true,
                                      id: filter.id,
                                      value: filter.value,
                                      parentId: null,
                                    },
                                  ]
                                : []),
                              // Remove old items from this category
                              ...prev.filter(
                                (f) =>
                                  filter.options.findIndex(
                                    (option) => option.id === f.id
                                  ) === -1
                              ),
                              ...filter.options.map((option) => ({
                                isRoot: false,
                                id: option.id,
                                parentId: filter.id,
                                value: option.value,
                              })),
                            ]);
                          }
                        }}
                      >
                        <p
                          className={
                            'text-black dark:text-white font-medium uppercase tracking-wide text-sm'
                          }
                        >
                          {filter.title}
                        </p>
                      </CheckboxWithLabel>
                    </div>
                    {filter.options.map((option, i) => {
                      const isOptionChecked =
                        checked.findIndex((el) => el.id === option.id) !== -1;

                      return (
                        <div
                          className="flex items-center justify-between py-2"
                          key={i}
                        >
                          <CheckboxWithLabel
                            isChecked={isRootChecked || isOptionChecked}
                            onClick={() => {
                              if (isOptionChecked && isRootChecked) {
                                // Remove filter and parent
                                return setChecked((prev) =>
                                  prev
                                    .filter((el) => el.id !== option.id)
                                    .filter((el) => el.id !== filter.id)
                                );
                              } else if (isOptionChecked) {
                                // Only option is checked, not parent
                                return setChecked((prev) =>
                                  prev.filter((el) => el.id !== option.id)
                                );
                              } else {
                                // Option is not checked
                                const isAllOptionsCheckedInCategory =
                                  checked.filter(
                                    (el) => el.parentId === filter.id
                                  ).length ===
                                  filters.find((el) => el.id === filter.id)!
                                    .options.length -
                                    1;

                                const newState = [
                                  {
                                    isRoot: false,
                                    id: option.id,
                                    parentId: filter.id,
                                    value: option.value,
                                  },

                                  ...checked,
                                ];
                                if (isAllOptionsCheckedInCategory) {
                                  newState.push({
                                    isRoot: true,
                                    id: filter.id,
                                    parentId: null,
                                    value: option.value,
                                  });
                                }
                                setChecked(newState);
                              }
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <p className={'text-black pl-3 dark:text-white'}>
                                {option.name}{' '}
                                <span className={'text-grey-70  text-xs'}>
                                  ({option.code})
                                </span>
                              </p>
                            </div>
                          </CheckboxWithLabel>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* REFERENCE ELEMENT */}
      <div
        className="w-full h-full flex items-center justify-between pl-4 cursor-pointer"
        ref={setReferenceEl}
        onClick={() => setIsVisible((prev) => !prev)}
      >
        {width === 1 ? (
          <Icon svgName="filter" />
        ) : (
          <div className="w-full items-center justify-between flex">
            <div className="flex items-center gap-3">
              <Icon svgName="filter" />
              <p className="text-sm">Filters</p>
              <div className="w-6 h-6 bg-grey-5 flex items-center justify-center rounded-full text-grey-70">
                {checked.filter((el) => !el.isRoot).length}
              </div>
            </div>
            <div>
              <Icon svgName="arrowDown" className="dark:text-white" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TreeFilter;
